import { useCallback, useEffect, useRef, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { getCaretPosition, CaretPosition } from "@/lib/textUtils";
import { cn } from "@/lib/utils";


export interface TextEditorTriggerEvent {
  trigger: string;
  caret: CaretPosition | null;
  selectionStart: number;
  selectionEnd: number;
  value: string;
}

interface TextEditorProps {
  content: string;
  onContentChange: (content: string) => void;
  className?: string;
  readOnly?: boolean;
  tags?: string[];
  onTagInput?: (tag: string) => void;
  onSave?: (text: string) => void;
  autosave?: boolean;
  autoheight?: boolean;
  scrollable?: boolean;
  placeholder?: string;
  triggers?: string[];
  onTrigger?: (event: TextEditorTriggerEvent) => void;
  actions?: React.ReactNode;
}

export function TextEditor({
  content,
  onContentChange,
  className = "",
  readOnly = false,
  tags = [],
  onTagInput,
  onSave,
  autosave = false,
  autoheight = false,
  scrollable = false,
  placeholder = "",
  triggers,
  onTrigger,
  actions,
}: TextEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState<string>(content);
  const lastSavedRef = useRef<string>(content);

  // Auto-resize textarea to fit content
  const adjustHeight = useCallback(() => {
    if (autoheight) {
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }
  }, [autoheight]);

  // Focus editor on mount and move cursor to end
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.focus();
      const length = textarea.value.length;
      textarea.setSelectionRange(length, length);
      adjustHeight();
    }
  }, [adjustHeight]);

  useEffect(() => {
    // Only update if content changed externally (not from our own save)
    // This prevents overwriting user input when auto-save completes
    if (text !== content && content !== lastSavedRef.current) {
      setText(content);
      lastSavedRef.current = content;
    }
  }, [content, text]);

  // Debounced autosave
  useEffect(() => {
    if (!autosave || !onSave || text === content) return;

    const timer = setTimeout(() => {
      onSave(text);
      lastSavedRef.current = text; // Track what we saved
    }, 200);

    return () => clearTimeout(timer);
  }, [text, content, autosave, onSave]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (text !== e.target.value) {
      let newContent = e.target.value;

      if (tags) {
        tags.forEach((tag) => {
          if (newContent.includes(`${tag}`)) {
            newContent = newContent.replace(`${tag}`, "");
            onTagInput?.(tag);
          }
        });
      }

      setText(newContent);
      onContentChange?.(newContent);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      onSave?.(text);
      return;
    }

    if (
      triggers &&
      onTrigger &&
      triggers.includes(e.key) &&
      !e.metaKey &&
      !e.ctrlKey &&
      !e.altKey
    ) {
      const textarea = textareaRef.current;
      if (!textarea) return;

      e.preventDefault();
      const caret = getCaretPosition(textarea);
      const selectionStart = textarea.selectionStart ?? 0;
      const selectionEnd = textarea.selectionEnd ?? selectionStart;
      onTrigger?.({
        trigger: e.key,
        caret,
        selectionStart,
        selectionEnd,
        value: textarea.value,
      });
    }
  };

  return (
    <div className={cn("relative", !autoheight && "h-full")}>
      <Textarea
        ref={textareaRef}
        readOnly={readOnly}
        className={`min-h-[100px] shadow-xs resize-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border focus-visible:border-zinc-200 placeholder:text-zinc-300 ${scrollable ? 'overflow-y-auto scrollbar-hidden' : 'overflow-hidden'} ${className}`}
        value={text}
        placeholder={placeholder}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
      {actions && (
        <div className="absolute bottom-2 right-2 pointer-events-auto">
          {actions}
        </div>
      )}
    </div>
  );
}
